import { ActionMenu, Icon } from '@dittolive/anvil'
import { CaretDownIcon, PlusIcon } from '@phosphor-icons/react'
import React from 'react'

export type BreadcrumbSwitcherItem = {
  id: string
  label: string
  onClick: () => void
  active?: boolean
}

type ActionItem = {
  label: string
  onClick: () => void
}

type Props = {
  /** Text shown in the closed trigger, e.g. the current database name. */
  currentLabel: string
  /** Optional entry pinned above the list (e.g. "All databases"). */
  headerItem?: ActionItem
  /** Switch targets. */
  items: BreadcrumbSwitcherItem[]
  /** Optional entry pinned below the list (e.g. "New database"). */
  footerItem?: ActionItem
  /** Called when the dropdown opens or closes. Useful for refetching. */
  onOpenChange?: (open: boolean) => void
}

/**
 * Breadcrumb segment that doubles as a switcher: the current entity's name
 * with a caret, opening a dropdown of alternatives. Grouped so the header and
 * footer actions render with real menu separators.
 */
export function BreadcrumbSwitcherMenu({
  currentLabel,
  headerItem,
  items,
  footerItem,
  onOpenChange,
}: Props) {
  if (items.length === 0 && !headerItem && !footerItem) {
    return (
      <span className="text-foreground-subtle text-sm">{currentLabel}</span>
    )
  }

  const groups = [
    ...(headerItem
      ? [
          {
            items: [
              {
                id: 'header',
                onClick: headerItem.onClick,
                label: headerItem.label,
              },
            ],
          },
        ]
      : []),
    ...(items.length > 0 ? [{ items }] : []),
    ...(footerItem
      ? [
          {
            items: [
              {
                id: 'footer',
                onClick: footerItem.onClick,
                label: footerItem.label,
                icon: PlusIcon,
                iconPosition: 'right' as const,
              },
            ],
          },
        ]
      : []),
  ]

  return (
    <ActionMenu
      trigger={
        <button
          type="button"
          className="text-foreground-subtle hover:text-foreground-normal focus-visible:outline-focus-outline flex items-center gap-1 text-sm outline-none focus-visible:outline-2 focus-visible:-outline-offset-2"
        >
          {currentLabel}
          <Icon svg={<CaretDownIcon />} className="size-3" />
        </button>
      }
      groups={groups}
      align="start"
      onOpenChange={onOpenChange}
    />
  )
}
